<template>
  <v-form v-model="valid">
    <input v-model="deadline_edit.account_id" type="hidden" />

    <v-chip
      v-if="deadline_edit.account_id"
      class="mb-4 ml-12"
      prepend-icon="mdi-account"
      color="primary"
      label
    >
      {{ deadline_edit.account_name || `Account #${deadline_edit.account_id}` }}
    </v-chip>

    <v-menu
      v-model="datepicker"
      :close-on-content-click="false"
      max-width="290"
    >
      <template #activator="{ props }">
        <v-text-field
          :model-value="computedDateFormatted"
          clearable
          label="Datum taak"
          readonly
          required
          prepend-icon="mdi-calendar"
          outlined
          v-bind="props"
          @click:clear="deadline_edit.deadline = null"
        ></v-text-field>
      </template>
      <v-date-picker
        v-model="deadline_edit.deadline"
        @update:model-value="datepicker = false"
      ></v-date-picker>
    </v-menu>

    <v-select
      v-model="deadline_edit.user_id"
      :items="usersWithBlankOption"
      label="Taak eigenaar (optioneel)"
      dense
      prepend-icon="mdi-account"
      outlined
      item-title="name"
      item-value="id"
      clearable
    ></v-select>

    <v-select
      v-model="deadline_edit.deadline_category_id"
      :items="categoryOptions"
      label="Categorie"
      dense
      prepend-icon="mdi-tag"
      outlined
      item-title="name"
      item-value="id"
      :loading="categoriesStore.loading"
      :error-messages="categoryErrors"
      required
      @blur="v$.deadline_edit.deadline_category_id.$touch()"
    ></v-select>

    <div class="d-flex ga-2">
      <v-text-field
        v-model.number="duration_hours"
        type="number"
        label="Uren"
        min="0"
        max="8"
        step="1"
        class="flex-1-1"
        prepend-icon="mdi-clock-outline"
      ></v-text-field>
      <v-text-field
        v-model.number="duration_minutes"
        type="number"
        label="Minuten"
        min="0"
        max="59"
        step="1"
        class="flex-1-1"
        prepend-icon="mdi-clock-outline"
      ></v-text-field>
    </div>

    <v-textarea
      v-model="deadline_edit.description"
      label="Taak omschrijving"
      outlined
      dense
      rows="1"
      auto-grow
      prepend-icon="mdi-playlist-edit"
      :error-messages="descriptionErrors"
      @input="v$.deadline_edit.description.$touch()"
      @blur="v$.deadline_edit.description.$touch()"
    ></v-textarea>

    <v-btn
      class="mt-2"
      block
      bottom
      color="primary"
      :loading="loading"
      @click="submit"
      >{{ submitButtonText }}</v-btn
    >
  </v-form>
</template>

<script>
import { required, minValue, maxValue, numeric } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import moment from "moment";
import { useDeadlinesStoreRefactor } from "@/stores/deadlinesStoreRefactor";
import { useAlertStore } from "@/stores/alertStore";
import { useUsersStoreRefactor } from "@/stores/usersStoreRefactor";
import { useAccountsStoreRefactor } from "@/stores/accountsStoreRefactor";
import { useDeadlineCategoriesStoreRefactor } from "@/stores/deadlineCategoriesStoreRefactor";
import { useFormsStore } from "@/stores/formsStore";
import { useAuthStoreRefactor } from "@/stores/authStoreRefactor";
import { ref, computed, reactive, watch, onMounted } from "vue";
import { useRouter } from "vue-router";

export default {
  name: "DeadlineForm",
  props: {
    deadline: {
      type: Object,
      required: true,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    submitButtonText: {
      type: String,
      default: "Opslaan",
    },
    popup: Boolean,
  },
  emits: ["saved"],
  setup(props, { emit }) {
    const deadlinesStore = useDeadlinesStoreRefactor();
    const alertStore = useAlertStore();
    const usersStore = useUsersStoreRefactor();
    const accountsStore = useAccountsStoreRefactor();
    const categoriesStore = useDeadlineCategoriesStoreRefactor();
    const formsStore = useFormsStore();
    const authStore = useAuthStoreRefactor();
    const router = useRouter();

    const valid = ref(false);
    const loading = ref(false);
    const datepicker = ref(false);

    const duration_hours = ref(null);
    const duration_minutes = ref(null);

    const deadline_edit = reactive({
      id: props.deadline.id || null,
      deadline:
        props.deadline.deadline || moment().add(1, "day").format("YYYY-MM-DD"),
      description: props.deadline.description || "",
      user_id: props.deadline.user_id ?? null,
      account_id: props.deadline.account_id || "",
      account_name: props.deadline.account_name || "",
      deadline_category_id: props.deadline.deadline_category_id || null,
      duration_seconds: props.deadline.duration_seconds ?? null,
    });

    onMounted(async () => {
      if (usersStore.users.length === 0) {
        await usersStore.getAll();
      }
      if (!categoriesStore.categories.length) {
        await categoriesStore.fetchAll();
      }
      if (deadline_edit.duration_seconds != null) {
        duration_hours.value = Math.floor(
          deadline_edit.duration_seconds / 3600,
        );
        duration_minutes.value = Math.floor(
          (deadline_edit.duration_seconds % 3600) / 60,
        );
      }
      if (accountsStore.accounts.length === 0 && deadline_edit.account_id) {
        await accountsStore.fetchById(deadline_edit.account_id);
      }
    });

    watch(
      () => props.deadline,
      (newDeadline) => {
        if (newDeadline) {
          Object.assign(deadline_edit, {
            id: newDeadline.id || null,
            deadline:
              newDeadline.deadline ||
              moment().add(1, "day").format("YYYY-MM-DD"),
            description: newDeadline.description || "",
            user_id: newDeadline.user_id ?? null,
            account_id: newDeadline.account_id || "",
            account_name: newDeadline.account_name || "",
            deadline_category_id: newDeadline.deadline_category_id || null,
            duration_seconds: newDeadline.duration_seconds ?? null,
          });

          duration_hours.value =
            newDeadline.duration_seconds != null
              ? Math.floor(newDeadline.duration_seconds / 3600)
              : null;
          duration_minutes.value =
            newDeadline.duration_seconds != null
              ? Math.floor((newDeadline.duration_seconds % 3600) / 60)
              : null;
        }
      },
      { immediate: true },
    );

    const rules = {
      deadline_edit: {
        deadline: { required },
        description: { required },
        deadline_category_id: { required },
      },
      duration_hours: { numeric, minValue: minValue(0), maxValue: maxValue(8) },
      duration_minutes: {
        numeric,
        minValue: minValue(0),
        maxValue: maxValue(59),
      },
    };

    const v$ = useVuelidate(rules, {
      deadline_edit,
      duration_hours,
      duration_minutes,
    });

    const usersWithBlankOption = computed(() => {
      const activeUsers = usersStore.activeUsersInCurrentTenant;
      return [
        { id: null, name: "Voor iedereen (geen eigenaar)" },
        ...activeUsers,
      ];
    });

    const categoryOptions = computed(() => categoriesStore.categories);

    const computedDateFormatted = computed(() => {
      return deadline_edit.deadline
        ? moment(deadline_edit.deadline).format("DD-MM-YYYY")
        : "";
    });

    const descriptionErrors = computed(() => {
      return v$.value.deadline_edit.description.$error
        ? ["Beschrijving is verplicht."]
        : [];
    });

    const categoryErrors = computed(() => {
      return v$.value.deadline_edit.deadline_category_id.$error
        ? ["Categorie is verplicht."]
        : [];
    });

    const submit = async () => {
      loading.value = true;
      const isFormCorrect = await v$.value.$validate();
      if (!isFormCorrect) {
        loading.value = false;
        return;
      }

      try {
        let duration_seconds = null;
        if (duration_hours.value != null || duration_minutes.value != null) {
          const h = Number(duration_hours.value || 0);
          const m = Number(duration_minutes.value || 0);
          duration_seconds = h * 3600 + m * 60;
          if (duration_seconds > 28800) duration_seconds = 28800;
        }

        const submitData = {
          ...deadline_edit,
          deadline: moment(deadline_edit.deadline).format("YYYY-MM-DD"),
          duration_seconds,
        };

        if (deadline_edit.id) {
          await deadlinesStore.update(submitData);
          alertStore.setAlert("Deadline succesvol bijgewerkt", "success");
        } else {
          await deadlinesStore.create(submitData);
          alertStore.setAlert("Deadline succesvol aangemaakt", "success");
        }

        formsStore.$reset();

        if (!props.popup) {
          deadline_edit.id ? emit("saved") : router.replace("/");
        }
      } catch (error) {
        alertStore.setAlert(
          "Er is een fout opgetreden bij het opslaan van de deadline",
          "error",
        );
      } finally {
        loading.value = false;
      }
    };

    return {
      valid,
      loading,
      deadline_edit,
      datepicker,
      usersWithBlankOption,
      categoryOptions,
      categoriesStore,
      computedDateFormatted,
      descriptionErrors,
      categoryErrors,
      duration_hours,
      duration_minutes,
      submit,
      v$,
    };
  },
};
</script>
